import React, { useMemo, useRef } from "react";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {
  AMARELO_PARTICIPACAO,
  AZUL,
  BORDA,
  FONTE,
  FUNDO_REGISTRO_PAY,
  MEDIDAS_INDICADOR,
  NAVY,
  SUAVE,
  TEXTO_SOBRE_COR,
  TRILHO_PARTICIPACAO,
  VERDE_VARIACAO,
  VERMELHO_VARIACAO,
  estilos,
} from "../estilos/tokens";
import EstadoRequisicao from "../componentes/EstadoRequisicao";
import ValorAjustavel from "../componentes/ValorAjustavel";
import { useFiltroHome } from "../contexto/filtroHome";
import { rotuloPeriodo } from "../contexto/semana";
import { useRequisicao } from "../hooks/useRequisicao";
import { useLarguraElemento, useMedidasElemento } from "../hooks/useLarguraElemento";
import { useAlturaDisponivel } from "../hooks/useAlturaDisponivel";
import {
  AjustesComparativo,
  buscarComparativo,
  ComparativoResponse,
  DesempenhoOperadora,
  DiaComparativo,
  IndicadorComparativo,
} from "../../../../services/home";
import Grafico from "../componentes/Grafico";
import { inteiro, percentualUmaCasa, reais } from "../formatadores";
import { usePaletaAtiva } from "../../../../styles/theme/usePaletaAtiva";
import { LogoRegistroPay, SeloEmBreve } from "./registroPay";
import { calculaDestaques, colunasComparativo, comparativoVazio, Destaque, montaOpcoesGrafico } from "./comparativoDados";

const SEM_DIAS: DiaComparativo[] = [];
const ALTURA_GRAFICO = 340;
const ALTURA_GRAFICO_LARGO = 490;
const ESPACO = 20;

const colunaFlex = { display: "flex", flexDirection: "column" as "column" };

const estilosAba = {
  cartao: { ...estilos.cartao, borderRadius: 10, minWidth: 0 },
  cartaoIndicador: {
    ...estilos.cartao,
    borderRadius: MEDIDAS_INDICADOR.raio,
    minWidth: 0,
    padding: MEDIDAS_INDICADOR.padding,
    display: "flex",
    flexDirection: "column" as "column",
  },
  titulo: {
    fontFamily: FONTE,
    fontSize: MEDIDAS_INDICADOR.titulo,
    fontWeight: 500,
    color: SUAVE,
    textTransform: "uppercase" as "uppercase",
    letterSpacing: 0.4,
    marginBottom: 18,
  },
  valor: {
    fontFamily: FONTE,
    fontSize: MEDIDAS_INDICADOR.valor,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 12,
  },
  detalhe: { fontFamily: FONTE, fontSize: MEDIDAS_INDICADOR.detalhe, color: SUAVE },
  linhaAjuste: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: 2,
    padding: "8px 0",
    fontFamily: FONTE,
    fontSize: 14,
    color: SUAVE,
  },
  valorAjuste: { fontSize: 16, fontWeight: 700 },
  variacao: (cor: string) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 16,
    fontFamily: FONTE,
    fontSize: 22,
    fontWeight: 700,
    color: cor,
  }),
  vsAnterior: { fontFamily: FONTE, fontSize: 13, color: SUAVE, marginTop: 4 },
  tituloCartao: {
    fontFamily: FONTE,
    fontSize: MEDIDAS_INDICADOR.tituloCartao,
    fontWeight: 700,
    color: AZUL,
  },
  cabecalhoTabela: {
    fontFamily: FONTE,
    fontSize: 13,
    fontWeight: 500,
    color: SUAVE,
    textTransform: "uppercase" as "uppercase",
    letterSpacing: 0.4,
    textAlign: "left" as "left",
    padding: "0 0 10px",
    borderBottom: `1px solid ${BORDA}`,
    background: "transparent",
    whiteSpace: "nowrap" as "nowrap",
    wordBreak: "normal" as "normal",
  },
  celula: {
    fontFamily: FONTE,
    fontSize: 16,
    color: NAVY,
    padding: "14px 0 12px",
    borderBottom: `1px solid ${BORDA}`,
    verticalAlign: "middle" as "middle",
    wordBreak: "normal" as "normal",
  },
  rotuloDestaque: {
    fontFamily: FONTE,
    fontSize: 13,
    fontWeight: 500,
    color: SUAVE,
    textTransform: "uppercase" as "uppercase",
    letterSpacing: 0.4,
  },
  valorDestaque: { fontFamily: FONTE, fontSize: 19, fontWeight: 700, color: NAVY, marginTop: 4 },
  textoRegistroPay: { fontFamily: FONTE, fontSize: 14, fontWeight: 500, lineHeight: 1.6, color: TEXTO_SOBRE_COR },
};

const iconeVariacao = { fontSize: 26 };

const Variacao = ({ valor }: { valor?: number | null }) => {
  if (valor == null) {
    return null;
  }
  const arredondado = Math.round(valor * 10) / 10;
  const percentual = percentualUmaCasa(Math.abs(valor));
  const conteudo =
    arredondado > 0 ? (
      <div style={estilosAba.variacao(VERDE_VARIACAO)}>
        <ArrowUpwardIcon style={iconeVariacao} />
        {`+${percentual}`}
      </div>
    ) : arredondado < 0 ? (
      <div style={estilosAba.variacao(VERMELHO_VARIACAO)}>
        <ArrowDownwardIcon style={iconeVariacao} />
        {`-${percentual}`}
      </div>
    ) : (
      <div style={estilosAba.variacao(SUAVE)}>{percentual}</div>
    );
  return (
    <div>
      {conteudo}
      <div style={estilosAba.vsAnterior}>vs. semana anterior</div>
    </div>
  );
};

interface CartaoIndicadorProps {
  titulo: string;
  indicador: IndicadorComparativo;
  detalhe: string;
  children?: React.ReactNode;
}

const CartaoIndicador = ({ titulo, indicador, detalhe, children }: CartaoIndicadorProps) => (
  <div style={estilosAba.cartaoIndicador}>
    <div style={estilosAba.titulo}>{titulo}</div>
    <ValorAjustavel style={estilosAba.valor}>{reais(indicador.valor)}</ValorAjustavel>
    <div style={estilosAba.detalhe}>{detalhe}</div>
    <Variacao valor={indicador.variacao} />
    {children}
  </div>
);

const transacoes = (quantidade: number) => `${inteiro(quantidade)} ${quantidade === 1 ? "transação" : "transações"}`;

const SemAntecipacao = () => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginTop: 18 }}>
    <InfoOutlinedIcon style={{ fontSize: 22, color: SUAVE, flexShrink: 0, marginTop: 1 }} />
    <span style={{ ...estilosAba.detalhe, lineHeight: 1.4 }}>Nenhuma antecipação na semana</span>
  </div>
);

const CartaoAjustes = ({ creditos, debitos, liquido, variacao }: AjustesComparativo) => (
  <div style={estilosAba.cartaoIndicador}>
    <div style={estilosAba.titulo}>Ajustes</div>
    <ValorAjustavel style={{ ...estilosAba.valor, marginBottom: 8 }}>{reais(liquido)}</ValorAjustavel>
    <Variacao valor={variacao} />
    <div style={{ ...estilosAba.linhaAjuste, borderBottom: `1px solid ${BORDA}` }}>
      <span>Créditos</span>
      <ValorAjustavel style={{ ...estilosAba.valorAjuste, color: VERDE_VARIACAO }}>{`+ ${reais(creditos)}`}</ValorAjustavel>
    </div>
    <div style={estilosAba.linhaAjuste}>
      <span>Débitos</span>
      <ValorAjustavel style={{ ...estilosAba.valorAjuste, color: VERMELHO_VARIACAO }}>{`- ${reais(debitos)}`}</ValorAjustavel>
    </div>
  </div>
);

const PeriodosComparados = ({ dados }: { dados: ComparativoResponse }) => {
  const { inicio, fim, inicioAnterior, fimAnterior } = dados;
  if (!inicio || !fim || !inicioAnterior || !fimAnterior) {
    return <div style={{ marginBottom: 10 }} />;
  }
  return (
    <div style={{ ...estilosAba.detalhe, marginBottom: 10 }}>
      {`${rotuloPeriodo(inicio, fim)} vs. ${rotuloPeriodo(inicioAnterior, fimAnterior)}`}
    </div>
  );
};

const larguraParticipacao = (participacao: number) => `${Math.min(100, Math.max(0, participacao))}%`;

const TabelaOperadoras = ({ operadoras, compacta }: { operadoras: DesempenhoOperadora[]; compacta: boolean }) => {
  if (operadoras.length === 0) {
    return <div style={{ ...estilos.semDados, minHeight: 160 }}>Nenhuma venda no período.</div>;
  }
  return (
    <table style={{ width: "100%", height: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={{ ...estilosAba.cabecalhoTabela, paddingRight: 12 }}>Operadora</th>
          <th style={estilosAba.cabecalhoTabela}>Vendas</th>
          <th style={{ ...estilosAba.cabecalhoTabela, textAlign: "right" }}>{compacta ? "Part." : "Participação"}</th>
        </tr>
      </thead>
      <tbody>
        {operadoras.map((o) => (
          <tr key={o.nome}>
            <td style={{ ...estilosAba.celula, fontSize: compacta ? 14 : 17, fontWeight: 700, paddingRight: 12 }}>{o.nome}</td>
            <td style={{ ...estilosAba.celula, fontSize: compacta ? 14 : 16, width: "50%" }}>
              <div style={{ whiteSpace: "nowrap", marginBottom: 6 }}>{reais(o.valor)}</div>
              <div style={{ height: 10, maxWidth: 212, borderRadius: 5, background: TRILHO_PARTICIPACAO }}>
                <div
                  style={{
                    height: 10,
                    borderRadius: 5,
                    background: AMARELO_PARTICIPACAO,
                    width: larguraParticipacao(o.participacao),
                  }}
                />
              </div>
            </td>
            <td style={{ ...estilosAba.celula, fontSize: 15, textAlign: "right", whiteSpace: "nowrap", paddingLeft: 12 }}>
              {percentualUmaCasa(o.participacao)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ItemDestaque = ({ rotulo, destaque, borda }: { rotulo: string; destaque: Destaque | null; borda: boolean }) => (
  <div style={{ minWidth: 0, padding: "2px 14px", borderLeft: borda ? `1px solid ${BORDA}` : "none" }}>
    <div style={estilosAba.rotuloDestaque}>{rotulo}</div>
    {destaque ? (
      <>
        <div style={{ fontFamily: FONTE, fontSize: 16, fontWeight: 700, color: AZUL, marginTop: 8 }}>{destaque.dia}</div>
        <ValorAjustavel style={estilosAba.valorDestaque}>{reais(destaque.valor)}</ValorAjustavel>
      </>
    ) : (
      <div style={{ ...estilosAba.detalhe, fontSize: 14, marginTop: 8 }}>Sem movimento</div>
    )}
  </div>
);

const RegistroPay = ({ posicao }: { posicao: React.CSSProperties }) => (
  <div
    style={{
      ...estilosAba.cartao,
      ...posicao,
      background: FUNDO_REGISTRO_PAY,
      border: "none",
      padding: "32px 24px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      gap: 18,
    }}
  >
    <LogoRegistroPay largura={131} />
    <div style={estilosAba.textoRegistroPay}>Quanto do seu futuro financeiro já está comprometido?</div>
    <div style={estilosAba.textoRegistroPay}>Antecipações, garantias, cessões e previsão de recebimentos em uma visão integrada.</div>
    <SeloEmBreve />
  </div>
);

const posicoes = (colunas: number) => {
  if (colunas === 6) {
    return {
      grafico: { gridColumn: "1 / 4", gridRow: "span 2" },
      operadoras: { gridColumn: "4 / 6" },
      destaques: { gridColumn: "4 / 6" },
      registro: { gridColumn: "6 / 7", gridRow: "span 2" },
    };
  }
  if (colunas === 3) {
    return {
      grafico: { gridColumn: "1 / -1" },
      operadoras: { gridColumn: "1 / 3" },
      destaques: { gridColumn: "1 / 3" },
      registro: { gridColumn: "3 / 4", gridRow: "span 2" },
    };
  }
  const linhaInteira = { gridColumn: "1 / -1" };
  return { grafico: linhaInteira, operadoras: linhaInteira, destaques: linhaInteira, registro: linhaInteira };
};

const Comparativo = () => {
  const { filtro, semana } = useFiltroHome();
  const referencia = useRef<HTMLDivElement>(null);
  const colunas = colunasComparativo(useLarguraElemento(referencia));
  const { dados, carregando, erro, recarregar } = useRequisicao(
    () => buscarComparativo(filtro, semana),
    [filtro.operadoras, filtro.cnpjs, semana],
  );

  const largo = colunas === 6;
  const { referencia: refGrade, altura: alturaDisponivel } = useAlturaDisponivel();
  const { referencia: refAreaGrafico, altura: alturaAreaGrafico } = useMedidasElemento();

  const dias = dados ? dados.dias : SEM_DIAS;
  const alturaGrafico = largo ? alturaAreaGrafico || ALTURA_GRAFICO_LARGO : ALTURA_GRAFICO;
  const paleta = usePaletaAtiva();
  const opcoesGrafico = useMemo(() => montaOpcoesGrafico(dias, alturaGrafico, paleta), [dias, alturaGrafico, paleta]);
  const destaques = useMemo(() => calculaDestaques(dias), [dias]);

  const vazio = !!dados && comparativoVazio(dados);
  const posicao = posicoes(colunas);
  const destaquesEmLinha = colunas !== 1;

  return (
    <div ref={referencia}>
      <EstadoRequisicao carregando={carregando} erro={erro} vazio={vazio} onRecarregar={recarregar}>
        {dados && (
          <div
            ref={refGrade}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))`,
              gridTemplateRows: largo ? "auto 1fr auto" : undefined,
              minHeight: largo ? alturaDisponivel : undefined,
              gap: ESPACO,
            }}
          >
            <CartaoIndicador titulo="Vendas" indicador={dados.vendas} detalhe={transacoes(dados.vendas.quantidade)} />
            <CartaoIndicador
              titulo="Pagamentos"
              indicador={dados.pagamentos}
              detalhe={transacoes(dados.pagamentos.quantidade)}
            />
            <CartaoIndicador
              titulo="Antecipações"
              indicador={dados.antecipacoes}
              detalhe={transacoes(dados.antecipacoes.quantidade)}
            >
              {Math.abs(dados.antecipacoes.valor) < 1e-9 && <SemAntecipacao />}
            </CartaoIndicador>
            <CartaoAjustes {...dados.ajustes} />
            <CartaoIndicador titulo="Previsão de recebimento" indicador={dados.previsao} detalhe="Previstos na semana" />
            <CartaoIndicador
              titulo="Ticket médio"
              indicador={dados.ticketMedio}
              detalhe={transacoes(dados.ticketMedio.quantidade)}
            />

            <div style={{ ...estilosAba.cartao, ...posicao.grafico, ...colunaFlex, padding: "28px 28px 16px" }}>
              <div style={{ ...estilosAba.tituloCartao, marginBottom: 4 }}>Comparativo por dia da semana (Vendas)</div>
              <PeriodosComparados dados={dados} />
              {largo ? (
                <div ref={refAreaGrafico} style={{ flex: 1, position: "relative", minHeight: ALTURA_GRAFICO }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0 }}>
                    <Grafico opcoes={opcoesGrafico} />
                  </div>
                </div>
              ) : (
                <Grafico opcoes={opcoesGrafico} />
              )}
            </div>

            <div style={{ ...estilosAba.cartao, ...posicao.operadoras, ...colunaFlex, padding: "28px 22px 14px" }}>
              <div style={{ ...estilosAba.tituloCartao, marginBottom: 22 }}>Desempenho por operadora</div>
              <div style={{ flex: 1, overflowX: "auto", overflowY: "hidden" }}>
                <TabelaOperadoras operadoras={dados.operadoras} compacta={colunas === 1} />
              </div>
            </div>

            <RegistroPay posicao={posicao.registro} />

            <div style={{ ...estilosAba.cartao, ...posicao.destaques, ...colunaFlex, padding: "18px 2px 20px" }}>
              <div style={{ ...estilosAba.tituloCartao, padding: "0 18px", marginBottom: 14 }}>Destaques da semana</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: destaquesEmLinha ? "repeat(3, minmax(0, 1fr))" : "1fr",
                  alignContent: "center",
                  flex: 1,
                  rowGap: 16,
                }}
              >
                <ItemDestaque rotulo="Maior venda" destaque={destaques.maiorVenda} borda={false} />
                <ItemDestaque rotulo="Menor venda" destaque={destaques.menorVenda} borda={destaquesEmLinha} />
                <ItemDestaque rotulo="Maior recebimento" destaque={destaques.maiorRecebimento} borda={destaquesEmLinha} />
              </div>
            </div>
          </div>
        )}
      </EstadoRequisicao>
    </div>
  );
};

export default Comparativo;
